import { useCallback, useRef, useState } from 'react';

interface UseElementHeight {
  /**
   * Method for initial height calculation
   * 'getBoundingClientRect' | 'offsetHeight' | 'clientHeight' | 'scrollHeight'
   * @default 'getBoundingClientRect'
   */
  initialMethod?:
    | 'getBoundingClientRect'
    | 'offsetHeight'
    | 'clientHeight'
    | 'scrollHeight';
  /**
   * Method for dynamic height updates
   * 'getBoundingClientRect' | 'contentRect' | 'offsetHeight'
   * @default 'getBoundingClientRect'
   */
  dynamicMethod?: 'getBoundingClientRect' | 'contentRect' | 'offsetHeight';
  /**
   * Enable dynamic tracking after initial calculation
   * @default true
   */
  trackChanges?: boolean;
  /**
   * Debounce delay for dynamic updates (in milliseconds)
   * @default 0
   */
  debounceMs?: number;
}

export function useElementHeight<T extends HTMLElement = HTMLElement>(
  options: UseElementHeight = {}
) {
  const {
    initialMethod = 'getBoundingClientRect',
    dynamicMethod = 'getBoundingClientRect',
    trackChanges = true,
    debounceMs = 0,
  } = options;

  const [height, setHeight] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (!node) {
        setHeight(0);
        return;
      }

      let initialHeight = 0;
      switch (initialMethod) {
        case 'getBoundingClientRect':
          initialHeight = node.getBoundingClientRect().height;
          break;
        case 'offsetHeight':
          initialHeight = node.offsetHeight;
          break;
        case 'clientHeight':
          initialHeight = node.clientHeight;
          break;
        case 'scrollHeight':
          initialHeight = node.scrollHeight;
          break;
      }
      setHeight(initialHeight);

      if (!trackChanges) {
        return;
      }

      const updateHeight = (newHeight: number) => {
        if (debounceMs > 0) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            setHeight(newHeight);
          }, debounceMs);
        } else {
          setHeight(newHeight);
        }
      };

      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          let newHeight = 0;

          switch (dynamicMethod) {
            case 'getBoundingClientRect':
              newHeight = entry.target.getBoundingClientRect().height;
              break;
            case 'contentRect':
              newHeight = entry.contentRect.height;
              break;
            case 'offsetHeight':
              newHeight = (entry.target as HTMLElement).offsetHeight;
              break;
          }

          updateHeight(newHeight);
        }
      });

      resizeObserver.observe(node);
      observerRef.current = resizeObserver;
    },
    [initialMethod, dynamicMethod, trackChanges, debounceMs]
  );

  return { ref, height };
}
